import { rem } from '@mantine/core';
import type React from 'react';
import type { ShiftAnswerView } from '../api/types';

/** Card/input styles for the Prostovoljci screen. */
export const CARD: React.CSSProperties = {
  background: 'oklch(1 0 0)',
  border: '1px solid oklch(0.92 0.006 95)',
  borderRadius: 11,
};

export const SECTION_LABEL: React.CSSProperties = {
  fontSize: rem(12),
  letterSpacing: '0.04em',
  color: 'oklch(0.55 0.012 90)',
};

export const INPUT: React.CSSProperties = {
  padding: '7px 9px',
  borderRadius: 6,
  border: '1px solid oklch(0.9 0.006 95)',
  fontFamily: 'var(--rr-font)',
  fontSize: rem(13.5),
  background: 'oklch(1 0 0)',
  outline: 'none',
};

export const MONO = 'var(--rr-font-mono)';

/** Segmented-option colours. */
export const seg = (on: boolean): React.CSSProperties =>
  on
    ? {
        background: 'oklch(0.955 0.025 245)',
        color: 'oklch(0.34 0.07 245)',
        border: '1px solid oklch(0.85 0.04 245)',
      }
    : {
        background: 'oklch(1 0 0)',
        color: 'oklch(0.45 0.012 90)',
        border: '1px solid oklch(0.91 0.006 95)',
      };

export const SEG_BTN: React.CSSProperties = {
  cursor: 'pointer',
  padding: '6px 11px',
  borderRadius: 6,
  fontSize: rem(13),
  fontWeight: 500,
  font: undefined,
  fontFamily: 'var(--rr-font)',
};

export interface TagTone {
  fg: string;
  bg: string;
  bd: string;
  dot: string;
}

/** Tag colour palette. Tag.colour stores the dot value. */
export const TAG_TONES: TagTone[] = [
  { fg: 'oklch(0.38 0.09 245)', bg: 'oklch(0.95 0.03 245)', bd: 'oklch(0.86 0.05 245)', dot: 'oklch(0.55 0.11 245)' },
  { fg: 'oklch(0.36 0.07 155)', bg: 'oklch(0.95 0.032 155)', bd: 'oklch(0.85 0.055 155)', dot: 'oklch(0.55 0.09 155)' },
  { fg: 'oklch(0.42 0.08 75)', bg: 'oklch(0.96 0.035 75)', bd: 'oklch(0.88 0.055 75)', dot: 'oklch(0.66 0.11 75)' },
  { fg: 'oklch(0.44 0.11 25)', bg: 'oklch(0.955 0.03 25)', bd: 'oklch(0.88 0.05 25)', dot: 'oklch(0.57 0.13 25)' },
  { fg: 'oklch(0.4 0.09 300)', bg: 'oklch(0.955 0.03 300)', bd: 'oklch(0.87 0.05 300)', dot: 'oklch(0.53 0.11 300)' },
  { fg: 'oklch(0.38 0.06 200)', bg: 'oklch(0.95 0.028 200)', bd: 'oklch(0.85 0.045 200)', dot: 'oklch(0.57 0.08 200)' },
  { fg: 'oklch(0.44 0.1 350)', bg: 'oklch(0.955 0.03 350)', bd: 'oklch(0.88 0.05 350)', dot: 'oklch(0.6 0.12 350)' },
  { fg: 'oklch(0.4 0.012 90)', bg: 'oklch(0.962 0.005 95)', bd: 'oklch(0.89 0.008 95)', dot: 'oklch(0.6 0.012 90)' },
];

const NEUTRAL = TAG_TONES[TAG_TONES.length - 1]!;

/** Tone for a stored tag colour: exact dot match, else nearest oklch hue. */
export function tagTone(colour: string): TagTone {
  const exact = TAG_TONES.find((t) => t.dot === colour);
  if (exact) return exact;
  const m = /oklch\([\d.]+\s+([\d.]+)\s+([\d.]+)\)/.exec(colour);
  if (!m) return NEUTRAL;
  const chroma = Number(m[1]);
  const hue = Number(m[2]);
  if (chroma < 0.03) return NEUTRAL;
  let best = NEUTRAL;
  let bestDist = Infinity;
  for (const t of TAG_TONES.slice(0, -1)) {
    const th = Number(/\s([\d.]+)\)$/.exec(t.dot)?.[1] ?? 0);
    const d = Math.min(Math.abs(th - hue), 360 - Math.abs(th - hue));
    if (d < bestDist) {
      bestDist = d;
      best = t;
    }
  }
  return best;
}

/** A shift's answer as a chip (SPEC §1.1F) — on Profil's shift rows and in the board's detail panel. */
export const ANSWER_CHIP: Record<ShiftAnswerView['status'] | 'none', { text: string; fg: string; bg: string }> = {
  ok: { text: 'potrjena', fg: 'oklch(0.42 0.09 155)', bg: 'oklch(0.96 0.03 155)' },
  change: { text: 'sprememba', fg: 'oklch(0.48 0.1 70)', bg: 'oklch(0.965 0.04 80)' },
  cant: { text: 'ne more', fg: 'oklch(0.48 0.13 25)', bg: 'oklch(0.965 0.025 25)' },
  none: { text: 'brez odgovora', fg: 'oklch(0.52 0.012 90)', bg: 'oklch(0.965 0.004 95)' },
};
